import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Mail, Lock, User, Heart, Eye, EyeOff, X, Home, Sparkles, Check, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { login as apiLogin, register as apiRegister } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';

const LoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });
      toast({ 
        title: language === 'nl' ? 'Verzonden!' : 'Sent!', 
        description: language === 'nl' ? 'Check je inbox voor de nieuwe verificatielink.' : 'Check your inbox for the new verification link.' 
      });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await apiLogin(formData);
      login(response.user);
      toast({ 
        title: language === 'nl' ? 'Welkom terug!' : 'Welcome back!', 
        description: language === 'nl' ? 'Je bent succesvol ingelogd.' : 'You have successfully logged in.' 
      });
      
      // Navigate based on user type
      if (response.user?.userType === 'verhuurder') {
        navigate('/verhuurder/dashboard');
      } else {
        // For renters, go directly to swipe - SwipeInterfacePage will check for preferences
        navigate('/swipematch/swipe');
      }
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'nl' ? 'Er ging iets mis. Probeer het opnieuw.' : 'Something went wrong. Please try again.');
      
      // Check if it's a verification error
      if (error.response?.status === 403 && message.includes('geverifieerd')) {
        setVerificationEmail(formData.email);
        setShowVerificationMessage(true);
      } else {
        toast({ title: 'Oops!', description: message, variant: 'destructive' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Use window.location.origin for OAuth to work across all environments (preview, production, custom domains)
  const GOOGLE_AUTH_URL = window.location.origin;
  
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${GOOGLE_AUTH_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Google login mislukt');
      }
      
      login(data.user);
      
      if (data.token) {
        localStorage.setItem('roomhub_token', data.token);
        localStorage.setItem('roomhub_user', JSON.stringify(data.user));
      }
      
      toast({ title: language === 'nl' ? 'Welkom!' : 'Welcome!' });
      
      // Navigate based on user type
      if (data.user?.userType === 'verhuurder') {
        navigate('/verhuurder/dashboard');
      } else if (data.user?.hasPreferences === false) {
        navigate('/swipematch');
      } else {
        navigate('/swipematch/swipe');
      }
    } catch (error) {
      toast({ title: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
      <Header />
      <Toaster />
      <main className="flex-1 flex items-center justify-center py-12 px-4 pt-24">
        <Card className="w-full max-w-md rounded-3xl shadow-xl border border-[#E5E5E5] bg-white relative overflow-hidden">
          {/* Gradient accent */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#2E7D32]" />
          
          <Link to="/" className="absolute top-4 right-4 p-2 hover:bg-[#F7F7F7] rounded-full transition-colors">
            <X className="w-5 h-5 text-[#9E9E9E]" />
          </Link>

          <CardContent className="p-8">
            {/* Verification Message */}
            {showVerificationMessage ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-[#2A2A2A] mb-2">
                  {language === 'nl' ? 'Bevestig je email' : 'Verify your email'}
                </h2>
                <p className="text-[#9E9E9E] mb-6">
                  {language === 'nl' 
                    ? `We hebben een bevestigingslink gestuurd naar ${verificationEmail}. Klik op de link om in te loggen.`
                    : `We sent a verification link to ${verificationEmail}. Click the link to log in.`}
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    variant="outline"
                    className="w-full h-12 rounded-xl"
                  >
                    {resendingVerification ? (
                      <span className="animate-spin mr-2">⟳</span>
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {language === 'nl' ? 'Verstuur opnieuw' : 'Resend email'}
                  </Button>
                  <Button
                    onClick={() => setShowVerificationMessage(false)}
                    variant="ghost"
                    className="w-full h-12 rounded-xl text-[#9E9E9E]"
                  >
                    {language === 'nl' ? 'Terug naar inloggen' : 'Back to login'}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Logo */}
                <div className="text-center mb-8">
                  <Link to="/" className="inline-block">
                    <SwipeMatchLogo size="large" variant="color" className="justify-center" />
                  </Link>
                </div>

                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Welkom terug' : 'Welcome back'}</h1>
                  <p className="text-[#9E9E9E]">{language === 'nl' ? 'Log in om verder te swipen' : 'Log in to continue swiping'}</p>
                </div>

                {/* Native Google Login - shows only Google's own UI */}
                <div className="flex justify-center mb-6">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => toast({ title: 'Google login mislukt', variant: 'destructive' })}
                    useOneTap={false}
                    theme="outline"
                    size="large"
                    width="100%"
                    text={language === 'nl' ? 'signin_with' : 'signin_with'}
                    shape="rectangular"
                  />
                </div>

                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#E5E5E5]"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-[#9E9E9E]">{language === 'nl' ? 'of' : 'or'}</span>
                  </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#2A2A2A]">{language === 'nl' ? 'E-mail' : 'Email'}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                  <Input 
                    type="email" 
                    placeholder={language === 'nl' ? 'jouw@email.com' : 'your@email.com'} 
                    className="pl-12 h-12 bg-white border-[#E5E5E5] text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:border-[#2E7D32] focus:ring-[#2E7D32]/20" 
                    value={formData.email} 
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                    required 
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2A2A2A]">{language === 'nl' ? 'Wachtwoord' : 'Password'}</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                  <Input 
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-12 pr-12 h-12 bg-white border-[#E5E5E5] text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:border-[#2E7D32] focus:ring-[#2E7D32]/20" 
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                    required 
                    data-testid="login-password-input"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9E9E9E] hover:text-[#2A2A2A]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="text-right">
                  <Link to="/wachtwoord-vergeten" className="text-sm text-[#2E7D32] hover:text-[#256B2B]">
                    {language === 'nl' ? 'Wachtwoord vergeten?' : 'Forgot password?'}
                  </Link>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl" 
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? '...' : (language === 'nl' ? 'INLOGGEN' : 'LOG IN')}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
              <p className="text-center text-[#9E9E9E]">
                {language === 'nl' ? 'Nog geen account?' : 'No account yet?'}{' '}
                <Link to="/registreren" className="text-[#2E7D32] hover:text-[#256B2B] font-semibold">
                  {language === 'nl' ? 'Maak gratis aan' : 'Create free'}
                </Link>
              </p>
            </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    confirmPassword: '',
    userType: 'huurder'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleResendVerification = async () => {
    try {
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: registeredEmail })
      });
      toast({ 
        title: language === 'nl' ? 'Verzonden!' : 'Sent!', 
        description: language === 'nl' ? 'Check je inbox voor de verificatielink.' : 'Check your inbox for the verification link.' 
      });
    } catch (error) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({ title: 'Oops!', description: language === 'nl' ? 'Wachtwoorden komen niet overeen.' : 'Passwords do not match.', variant: 'destructive' });
      return;
    }

    if (formData.password.length < 8) {
      toast({ title: 'Oops!', description: 'Wachtwoord moet minimaal 8 karakters zijn.', variant: 'destructive' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await apiRegister({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        userType: formData.userType
      });
      
      // Check if verification is required
      if (response.requiresVerification) {
        setRegisteredEmail(response.email || formData.email);
        setRegistrationComplete(true);
      } else {
        // Old flow (should not happen with new backend)
        navigate('/login');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.';
      toast({ title: 'Oops!', description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Use window.location.origin for OAuth to work across all environments (preview, production, custom domains)
  const GOOGLE_API_URL = window.location.origin;
  const { login } = useAuth();
  
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${GOOGLE_API_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Google login mislukt');
      }
      
      login(data.user);
      
      if (data.token) {
        localStorage.setItem('roomhub_token', data.token);
        localStorage.setItem('roomhub_user', JSON.stringify(data.user));
      }
      
      toast({ title: 'Welkom bij Swipe je woning!' });
      
      // New user - go to onboarding
      navigate('/swipematch');
    } catch (error) {
      toast({ title: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // Show success screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
        <Header />
        <Toaster />
        <main className="flex-1 flex items-center justify-center py-12 px-4 pt-24">
          <Card className="w-full max-w-md rounded-3xl shadow-xl border border-[#E5E5E5] bg-white overflow-hidden">
            <div className="bg-gradient-to-r from-[#2E7D32] to-[#1F3A5F] p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {language === 'nl' ? 'Check je inbox!' : 'Check your inbox!'}
              </h2>
              <p className="text-white/80">
                {registeredEmail}
              </p>
            </div>
            <CardContent className="p-8 text-center">
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3 text-left">
                  <div className="w-6 h-6 rounded-full bg-[#2E7D32] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-[#2A2A2A]">
                    {language === 'nl' 
                      ? 'We hebben een bevestigingslink naar je email gestuurd'
                      : 'We sent a verification link to your email'}
                  </p>
                </div>
                <div className="flex items-start gap-3 text-left">
                  <div className="w-6 h-6 rounded-full bg-[#E5E5E5] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#9E9E9E] text-sm font-bold">2</span>
                  </div>
                  <p className="text-[#9E9E9E]">
                    {language === 'nl' 
                      ? 'Klik op de link om je account te activeren'
                      : 'Click the link to activate your account'}
                  </p>
                </div>
                <div className="flex items-start gap-3 text-left">
                  <div className="w-6 h-6 rounded-full bg-[#E5E5E5] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#9E9E9E] text-sm font-bold">3</span>
                  </div>
                  <p className="text-[#9E9E9E]">
                    {language === 'nl' 
                      ? 'Start met swipen naar je droomwoning!'
                      : 'Start swiping for your dream home!'}
                  </p>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  {language === 'nl' 
                    ? '⚠️ Check ook je spam folder als je de email niet ziet'
                    : '⚠️ Check your spam folder if you don\'t see the email'}
                </p>
              </div>
              
              <Button
                onClick={handleResendVerification}
                variant="outline"
                className="w-full h-12 rounded-xl mb-3"
              >
                <Mail className="w-4 h-4 mr-2" />
                {language === 'nl' ? 'Verstuur opnieuw' : 'Resend email'}
              </Button>
              
              <Link to="/login">
                <Button variant="ghost" className="w-full h-12 rounded-xl text-[#9E9E9E]">
                  {language === 'nl' ? 'Naar inloggen' : 'Go to login'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
      <Header />
      <Toaster />
      <main className="flex-1 flex items-center justify-center py-12 px-4 pt-24">
        <div className="w-full max-w-lg">
          {/* 100% Gratis Banner */}
          <div className="bg-[#2E7D32] rounded-t-3xl p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-white">
              <Sparkles className="w-5 h-5" />
              <span className="font-semibold">{language === 'nl' ? '100% GRATIS' : '100% FREE'}</span>
              <Sparkles className="w-5 h-5" />
            </div>
            <p className="text-white/80 text-sm mt-1">{language === 'nl' ? 'Alles gratis. Geen verborgen kosten. Geen creditcard nodig.' : 'Everything free. No hidden costs. No credit card needed.'}</p>
          </div>

          <Card className="rounded-t-none rounded-b-3xl shadow-xl border border-[#E5E5E5] bg-white relative overflow-hidden">
            <Link to="/" className="absolute top-4 right-4 p-2 hover:bg-[#F7F7F7] rounded-full transition-colors">
              <X className="w-5 h-5 text-[#9E9E9E]" />
            </Link>

            <CardContent className="p-8">
              {/* Logo */}
              <div className="text-center mb-6">
                <Link to="/" className="inline-block">
                  <SwipeMatchLogo size="large" variant="color" className="justify-center" />
                </Link>
              </div>

              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-[#2A2A2A] mb-2">{language === 'nl' ? 'Maak je account' : 'Create your account'}</h1>
                <p className="text-[#9E9E9E]">{language === 'nl' ? 'En vind je droomwoning in 2 weken' : 'And find your dream home in 2 weeks'}</p>
              </div>

              {/* Gratis benefits */}
              <div className="bg-[#2E7D32]/5 rounded-xl p-4 mb-6 border border-[#2E7D32]/20">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {(language === 'nl' ? [
                    'Onbeperkt swipen',
                    'Automatische berichten',
                    'Direct contact',
                    'Geen verborgen kosten'
                  ] : [
                    'Unlimited swipes',
                    'Auto messages',
                    'Direct contact',
                    'No hidden costs'
                  ]).map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2 text-[#2A2A2A] font-medium">
                      <Check className="w-4 h-4 text-[#2E7D32] flex-shrink-0" />
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>

              {/* Native Google Login */}
              <div className="flex justify-center mb-6">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast({ title: 'Google registratie mislukt', variant: 'destructive' })}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  width="100%"
                  text="signup_with"
                  shape="rectangular"
                />
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E5E5E5]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-[#9E9E9E]">{language === 'nl' ? 'of registreer met email' : 'or register with email'}</span>
                </div>
              </div>

              {/* User Type Selection */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, userType: 'huurder' })}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    formData.userType === 'huurder' 
                      ? 'border-[#2E7D32] bg-[#2E7D32]/5' 
                      : 'border-[#E5E5E5] hover:border-[#9E9E9E] bg-white'
                  }`}
                  data-testid="register-type-huurder"
                >
                  <Heart className={`w-6 h-6 mx-auto mb-2 ${formData.userType === 'huurder' ? 'text-[#2E7D32]' : 'text-[#9E9E9E]'}`} />
                  <span className={`text-sm font-medium ${formData.userType === 'huurder' ? 'text-[#2A2A2A]' : 'text-[#9E9E9E]'}`}>
                    {language === 'nl' ? 'Ik zoek een woning' : 'I\'m looking for a home'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, userType: 'verhuurder' })}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    formData.userType === 'verhuurder' 
                      ? 'border-[#1F3A5F] bg-[#1F3A5F]/5' 
                      : 'border-[#E5E5E5] hover:border-[#9E9E9E] bg-white'
                  }`}
                  data-testid="register-type-verhuurder"
                >
                  <Home className={`w-6 h-6 mx-auto mb-2 ${formData.userType === 'verhuurder' ? 'text-[#1F3A5F]' : 'text-[#9E9E9E]'}`} />
                  <span className={`text-sm font-medium ${formData.userType === 'verhuurder' ? 'text-[#2A2A2A]' : 'text-[#9E9E9E]'}`}>
                    {language === 'nl' ? 'Ik verhuur een woning' : 'I\'m renting out a property'}
                  </span>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#2A2A2A]">{language === 'nl' ? 'Volledige naam' : 'Full name'}</Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                    <Input 
                      type="text" 
                      placeholder={language === 'nl' ? 'Je naam' : 'Your name'}
                      className="pl-12 h-12 bg-white border-[#E5E5E5] text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:border-[#2E7D32] focus:ring-[#2E7D32]/20" 
                      value={formData.name} 
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                      required 
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#2A2A2A]">{language === 'nl' ? 'E-mail' : 'Email'}</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                    <Input 
                      type="email" 
                      placeholder={language === 'nl' ? 'jouw@email.com' : 'your@email.com'}
                      className="pl-12 h-12 bg-white border-[#E5E5E5] text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:border-[#2E7D32] focus:ring-[#2E7D32]/20" 
                      value={formData.email} 
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                      required 
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-[#2A2A2A]">{language === 'nl' ? 'Wachtwoord' : 'Password'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                      <Input 
                        type={showPassword ? 'text' : 'password'}
                        placeholder={language === 'nl' ? 'Min. 8 tekens' : 'Min. 8 chars'}
                        className="pl-12 h-12 bg-white border-[#E5E5E5] text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:border-[#2E7D32] focus:ring-[#2E7D32]/20" 
                        value={formData.password} 
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                        required 
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-[#2A2A2A]">{language === 'nl' ? 'Bevestig' : 'Confirm'}</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                      <Input 
                        type="password"
                        placeholder={language === 'nl' ? 'Herhaal' : 'Repeat'}
                        className="pl-12 h-12 bg-white border-[#E5E5E5] text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-xl focus:border-[#2E7D32] focus:ring-[#2E7D32]/20" 
                        value={formData.confirmPassword} 
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} 
                        required 
                        data-testid="register-confirm-password-input"
                      />
                    </div>
                  </div>
                </div>

                <p className="text-xs text-[#9E9E9E]">
                  {language === 'nl' ? 'Door je te registreren ga je akkoord met onze' : 'By registering you agree to our'}{' '}
                  <Link to="/voorwaarden" className="text-[#2E7D32] hover:underline">{language === 'nl' ? 'voorwaarden' : 'terms'}</Link>
                  {' '}{language === 'nl' ? 'en' : 'and'}{' '}
                  <Link to="/privacy" className="text-[#2E7D32] hover:underline">{language === 'nl' ? 'privacy policy' : 'privacy policy'}</Link>
                </p>

                <Button 
                  type="submit" 
                  className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white font-semibold rounded-xl text-lg" 
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? '...' : (
                    <>
                      {language === 'nl' ? 'START GRATIS' : 'START FREE'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
                <p className="text-center text-[#9E9E9E]">
                  {language === 'nl' ? 'Al een account?' : 'Already have an account?'}{' '}
                  <Link to="/login" className="text-[#2E7D32] hover:text-[#256B2B] font-semibold">
                    {language === 'nl' ? 'Log in' : 'Log in'}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export { LoginPage, RegisterPage };
